import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const CorrelationAnalysis = ({ events }) => {
  const [correlations, setCorrelations] = useState([]);

  useEffect(() => {
    analyzeCorrelations();
  }, [events]);

  const analyzeCorrelations = () => {
    // Analyze event correlations
    const ipGroups = {};
    const timeGroups = {};
    
    events?.forEach(event => {
      // Group by IP
      if (event?.ip_address) {
        if (!ipGroups?.[event?.ip_address]) ipGroups[event?.ip_address] = [];
        ipGroups?.[event?.ip_address]?.push(event);
      }
      
      // Group by time window (1 hour)
      const timeWindow = Math.floor(new Date(event?.created_at)?.getTime() / (1000 * 60 * 60));
      if (!timeGroups?.[timeWindow]) timeGroups[timeWindow] = [];
      timeGroups?.[timeWindow]?.push(event);
    });

    // Find correlations
    const foundCorrelations = [];
    
    // IP-based correlations
    Object.entries(ipGroups)?.forEach(([ip, ipEvents]) => {
      if (ipEvents?.length >= 3) {
        foundCorrelations?.push({
          type: 'ip_pattern',
          description: `Multiple events from IP ${ip}`,
          eventCount: ipEvents?.length,
          severity: ipEvents?.some(e => e?.severity === 'critical') ? 'critical' : 'warning',
          events: ipEvents
        });
      }
    });

    // Time-based correlations
    Object.entries(timeGroups)?.forEach(([timeWindow, timeEvents]) => {
      if (timeEvents?.length >= 5) {
        foundCorrelations?.push({
          type: 'time_cluster',
          description: `Event cluster detected in time window`,
          eventCount: timeEvents?.length,
          severity: 'warning',
          events: timeEvents
        });
      }
    });

    setCorrelations(foundCorrelations);
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'var(--color-error)',
      error: 'var(--color-error)',
      warning: 'var(--color-warning)',
      info: 'var(--color-info)'
    };
    return colors?.[severity] || 'var(--color-muted)';
  };

  return (
    <div className="space-y-6">
      {/* Correlation Overview */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-heading font-semibold text-foreground">
            Correlation Analysis
          </h3>
          <span className="text-sm text-muted-foreground">
            {correlations?.length || 0} patterns detected
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Network" size={18} className="text-primary" />
              <span className="text-sm font-medium text-foreground">Total Correlations</span>
            </div>
            <p className="text-2xl font-semibold text-foreground">{correlations?.length || 0}</p>
          </div>
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="MapPin" size={18} className="text-info" />
              <span className="text-sm font-medium text-foreground">IP Patterns</span>
            </div>
            <p className="text-2xl font-semibold text-foreground">
              {correlations?.filter(c => c?.type === 'ip_pattern')?.length || 0}
            </p>
          </div>
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Clock" size={18} className="text-warning" />
              <span className="text-sm font-medium text-foreground">Time Clusters</span>
            </div>
            <p className="text-2xl font-semibold text-foreground">
              {correlations?.filter(c => c?.type === 'time_cluster')?.length || 0}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {correlations?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Network" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No correlations detected</p>
            </div>
          ) : (
            correlations?.map((correlation, index) => (
              <div key={index} className="p-4 border border-border rounded-lg">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon
                      name={correlation?.type === 'ip_pattern' ? 'MapPin' : 'Clock'}
                      size={18}
                      color={getSeverityColor(correlation?.severity)}
                    />
                    <h4 className="text-sm font-medium text-foreground">{correlation?.description}</h4>
                  </div>
                  <span
                    className="px-2 py-0.5 rounded text-xs font-medium"
                    style={{
                      backgroundColor: `${getSeverityColor(correlation?.severity)}15`,
                      color: getSeverityColor(correlation?.severity)
                    }}
                  >
                    {correlation?.severity}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name="Activity" size={12} />
                    {correlation?.eventCount} correlated events
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Tag" size={12} />
                    {correlation?.type?.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {/* Visualization Placeholder */}
      <div className="card">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Relationship Mapping
        </h3>
        <div className="bg-muted/30 rounded-lg p-12 text-center">
          <Icon name="Network" size={64} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Visual relationship mapping coming soon</p>
        </div>
      </div>
    </div>
  );
};

export default CorrelationAnalysis;